import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import DashboardStats from './components/DashboardStats';
import FilterToolbar from './components/FilterToolbar';
import SessionCard from './components/SessionCard';
import EmptyState from './components/EmptyState';
import { supabase } from '../../lib/supabaseClient';

const InviteDashboard = () => {
  const navigate = useNavigate();
  const [statsExpanded, setStatsExpanded] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const PAGE_SIZE = 15;
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    search: '',
    eventType: 'all',
    status: 'all',
    sort: 'date-asc',
    dateFrom: '',
    dateTo: ''
  });

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const mapInviteRow = (row) => {
    if (!row) return null;
    return {
      id: row.id,
      title: row.title,
      sport: row.sport,
      eventType: row?.event_type || row?.session_type || row?.type || 'open_game',
      date: row.date,
      startTime: row.start_time,
      endTime: row.end_time,
      venue: row.venue,
      capacity: row.capacity,
      registered: row.registered,
      status: row.status,
      paymentStatus: row.payment_status,
      registrationPhase: row.registration_phase,
      price: row.price,
      token: row.token,
      coverImageUrl: row.cover_image_url || '',
      imageUrls: Array.isArray(row.image_urls) ? row.image_urls : [],
      createdAt: row.created_at
    };
  };

  const loadInvites = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const { data: sessionDataAuth } = await supabase.auth.getSession();
      const uid = sessionDataAuth?.session?.user?.id;
      if (!uid) {
        setSessions([]);
        setLoadError('로그인이 필요합니다.');
        setTotalCount(0);
        return;
      }

      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase
        .from('session_invites')
        .select('*', { count: 'exact' })
        .eq('client_id', uid);

      if (filters?.search) {
        query = query.ilike('title', `%${filters.search}%`);
      }

      if (filters?.eventType !== 'all') {
        query = query.eq('event_type', filters.eventType);
      }

      if (filters?.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.dateFrom) {
        query = query.gte('date', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('date', filters.dateTo);
      }

      switch (filters?.sort) {
        case 'date-asc':
          query = query.order('date', { ascending: true });
          break;
        case 'date-desc':
          query = query.order('date', { ascending: false });
          break;
        case 'participants-desc':
          query = query.order('registered', { ascending: false });
          break;
        case 'participants-asc':
          query = query.order('registered', { ascending: true });
          break;
        case 'created-asc':
          query = query.order('created_at', { ascending: true });
          break;
        case 'created-desc':
        default:
          query = query.order('created_at', { ascending: false });
          break;
      }

      const { data, error, count } = await query.range(from, to);
      if (error) {
        setSessions([]);
        setLoadError(error?.message || '초대장 목록을 불러오지 못했습니다.');
        setTotalCount(0);
        return;
      }
      setSessions((data || []).map(mapInviteRow).filter(Boolean));
      setTotalCount(count || 0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadInvites();
  }, [page, filters]);

  useEffect(() => {
    setPage(1);
  }, [
    filters?.search,
    filters?.eventType,
    filters?.status,
    filters?.sort,
    filters?.dateFrom,
    filters?.dateTo
  ]);

  const stats = useMemo(() => {
    const now = new Date();
    const upcoming = sessions?.filter(s => new Date(s.date) >= now && s?.status !== 'completed' && s?.status !== 'cancelled');
    const active = sessions?.filter(s => s?.status === 'open');
    const completed = sessions?.filter(s => s?.status === 'completed');
    const totalParticipants = sessions?.reduce((sum, s) => sum + (s?.registered || 0), 0);

    return {
      upcomingSessions: upcoming?.length,
      totalParticipants: totalParticipants,
      activeRegistrations: active?.length,
      completedSessions: completed?.length
    };
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    let filtered = [...sessions];

    if (filters?.search) {
      filtered = filtered?.filter(session =>
        session?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.eventType !== 'all') {
      filtered = filtered?.filter(session => session?.eventType === filters?.eventType);
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(session => session?.status === filters?.status);
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter(session => session?.date >= filters?.dateFrom);
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(session => session?.date <= filters?.dateTo);
    }

    filtered?.sort((a, b) => {
      switch (filters?.sort) {
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'participants-desc':
          return b?.registered - a?.registered;
        case 'participants-asc':
          return a?.registered - b?.registered;
        case 'created-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'created-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [sessions, filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      eventType: 'all',
      status: 'all',
      sort: 'date-asc',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleCreateNew = () => {
    setShowCreateMenu((prev) => !prev);
  };

  const handleSelectCreateType = (type) => {
    setShowCreateMenu(false);
    if (type === 'open_game') {
      navigate('/session-creation-wizard');
      return;
    }
    navigate(`/session-list-dashboard/event-create/${encodeURIComponent(type)}`);
  };

  const handleCopyLink = (session) => {
    setToastMessage(`${session?.title} 링크가 복사되었습니다`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleDeleteSession = async (session) => {
    const confirmed = window.confirm(`"${session?.title}" 세션을 삭제하시겠습니까?`);
    if (!confirmed) return;

    const { data: sessionDataAuth } = await supabase.auth.getSession();
    const uid = sessionDataAuth?.session?.user?.id;
    if (!uid) {
      alert('로그인이 필요합니다.');
      return;
    }

    const extractStoragePath = (publicUrl) => {
      const url = String(publicUrl || '').trim();
      if (!url) return '';
      const marker = '/storage/v1/object/public/session-images/';
      const idx = url.indexOf(marker);
      if (idx < 0) return '';
      return url.slice(idx + marker.length);
    };

    const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

    const safeRemovePaths = async (paths) => {
      const uniqPaths = uniq(paths.map((p) => String(p || '').trim()).filter(Boolean));
      if (uniqPaths.length === 0) return;
      await supabase.storage.from('session-images').remove(uniqPaths);
    };

    try {
      const baseFolder = `${uid}/sessions/${session?.id}`;

      // 1) delete by listing folder (preferred)
      try {
        const { data: listed, error: listError } = await supabase
          .storage
          .from('session-images')
          .list(baseFolder, { limit: 1000, offset: 0 });

        if (!listError && Array.isArray(listed) && listed.length > 0) {
          const filePaths = listed
            .filter((it) => it && it.name && !it.id)
            .map((it) => `${baseFolder}/${it.name}`);
          await safeRemovePaths(filePaths);
        }
      } catch {
        // ignore
      }

      // 2) fallback: delete by stored URLs if any
      const imageUrls = Array.isArray(session?.imageUrls) ? session.imageUrls : [];
      const coverUrl = String(session?.coverImageUrl || '').trim();
      const urlPaths = uniq([coverUrl, ...imageUrls]).map(extractStoragePath).filter(Boolean);
      await safeRemovePaths(urlPaths);
    } catch {
      // ignore storage cleanup failure
    }

    const { error } = await supabase
      .from('session_invites')
      .delete()
      .eq('id', session?.id);
    if (error) {
      alert(error?.message || '삭제에 실패했습니다.');
      return;
    }

    setSessions(prev => (prev || []).filter(s => s?.id !== session?.id));
    setToastMessage(`${session?.title} 세션이 삭제되었습니다`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const hasActiveFilters = 
    filters?.search !== '' ||
    filters?.eventType !== 'all' ||
    filters?.status !== 'all' ||
    filters?.dateFrom !== '' ||
    filters?.dateTo !== '';

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-6 px-4 max-w-6xl mx-auto">
          {loadError && (
            <div className="mb-6 md:mb-8 bg-error/10 border border-error/20 rounded-xl p-4">
              <p className="text-sm text-error">{loadError}</p>
            </div>
          )}

          <div className="md:hidden">
            <button
              className="w-full bg-card border border-border rounded-xl p-4 flex items-center justify-between hover-lift transition-athletic"
              onClick={() => setStatsExpanded(v => !v)}
            >
              <div>
                <p className="text-sm font-heading font-semibold text-foreground">이벤트 등록</p>
                <p className="text-xs caption text-muted-foreground mt-1">통계 보기</p>
              </div>
              <Icon name={statsExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
            </button>

            {statsExpanded && (
              <div className="mt-3">
                <DashboardStats stats={stats} />
              </div>
            )}
          </div>

          <div className="hidden md:block">
            <DashboardStats stats={stats} />
          </div>

          <div className="mt-6 md:mt-8 relative">
            <FilterToolbar
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
              onCreateNew={handleCreateNew}
              createButtonLabel="이벤트 등록"
              createMenuOpen={false}
              createMenuItems={[
                { type: 'open_game', label: '오픈 게임' },
                { type: 'lesson', label: '레슨' },
              ]}
              onSelectCreateType={handleSelectCreateType}
            />
          </div>

          <div className="mt-6 md:mt-8">
            {isLoading ? (
              <div className="bg-card border border-border rounded-xl p-8 md:p-12 text-center">
                <p className="text-sm md:text-base caption text-muted-foreground">불러오는 중...</p>
              </div>
            ) : filteredSessions?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredSessions?.map((session) => (
                  <SessionCard
                    key={session?.id}
                    session={session}
                    onCopyLink={handleCopyLink}
                    onDelete={handleDeleteSession}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                onCreateNew={handleCreateNew}
                hasFilters={hasActiveFilters}
                onResetFilters={handleResetFilters}
              />
            )}
          </div>

          {!isLoading && !loadError && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                이전
              </Button>
              <div className="text-sm caption text-muted-foreground">
                {page} / {totalPages}
              </div>
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                다음
              </Button>
            </div>
          )}
      </main>

      {showToast && (
        <div className="fixed bottom-4 right-4 z-300 animate-in slide-in-from-bottom-4 duration-250">
          <div className="bg-card border border-border rounded-lg shadow-xl px-4 py-3 flex items-center gap-3 min-w-64 max-w-sm">
            <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-foreground">{toastMessage}</p>
          </div>
        </div>
      )}

      {showCreateMenu && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setShowCreateMenu(false);
          }}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
          <div className="relative w-full max-w-sm bg-card border border-border rounded-2xl shadow-elevation-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <p className="text-base font-semibold text-foreground">이벤트 등록</p>
                <p className="text-xs text-muted-foreground caption mt-0.5">이벤트 종류를 선택하세요</p>
              </div>
              <button
                type="button"
                className="w-9 h-9 rounded-lg border border-border bg-background hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
                onClick={() => setShowCreateMenu(false)}
                aria-label="닫기"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 gap-2">
                {[
                  { type: 'open_game', label: '오픈 게임' },
                  { type: 'lesson', label: '레슨' },
                ].map((item) => (
                  <button
                    key={item.type}
                    type="button"
                    className="w-full flex items-center justify-between rounded-xl px-4 py-3 border border-border bg-background hover:bg-muted transition-athletic"
                    onClick={() => handleSelectCreateType(item.type)}
                  >
                    <span className="text-sm font-semibold text-foreground">{item.label}</span>
                    <span className="text-muted-foreground">›</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </OpenPlayToolsLayout>
  );
};

export default InviteDashboard;
